import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, Box, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { StatusBadge } from '../components/coreComponents/badge';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/coreComponents/button';
import { SecretValue } from '../components/coreComponents/secretValue';
import { DrawerModal, DrawerModalButtonsWrapper } from '../components/coreComponents/drawerModal';
import { SelectInput } from '../components/coreComponents/selectInput';

interface AccountProvisionDrawerProps {
  open: boolean;
  onClose: () => void;
  onProvisioned: () => void;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  organisationName: string;
  role: string;
}

interface ProvisionContext {
  jobId: string;
  password: string | null;
  email: string;
  linkedExistingUser: boolean;
}

export function AccountProvisionDrawer(props: AccountProvisionDrawerProps) {
  const [provision] = useMutation(provisionMutation);
  const { data: roleData, loading: rolesLoading } = useQuery(roleOptionsQuery);
  const [context, setContext] = useState<ProvisionContext | null>(null);

  const roleOptions = roleData?.organisationRoleTemplateOptions ?? [];

  const formik = useFormik<FormValues>({
    initialValues: { firstName: '', lastName: '', email: '', organisationName: '', role: '' },
    validate: values => {
      const errors: Partial<FormValues> = {};
      if (!values.firstName.trim()) errors.firstName = 'First name is required';
      if (!values.lastName.trim()) errors.lastName = 'Last name is required';
      if (!values.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errors.email = 'Enter a valid email address';
      }
      if (!values.organisationName.trim()) errors.organisationName = 'Company name is required';
      if (!values.role) errors.role = 'Role is required';
      return errors;
    },
    onSubmit: async (values, helpers) => {
      try {
        const response = await provision({
          variables: {
            input: {
              firstName: values.firstName.trim(),
              lastName: values.lastName.trim(),
              email: values.email.trim().toLowerCase(),
              organisationName: values.organisationName.trim(),
              role: values.role
            }
          }
        });

        const data = response.data?.accountProvision;
        if (data == null) throw new Error('No response from server.');

        setContext({
          jobId: data.jobId,
          password: data.temporaryPassword ?? null,
          email: values.email.trim().toLowerCase(),
          linkedExistingUser: data.linkedExistingUser
        });
        props.onProvisioned();
      } catch (err: any) {
        helpers.setStatus(err.message ?? 'Failed to provision account');
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    setContext(null);
    props.onClose();
  };

  return (
    <DrawerModal open={props.open} onClose={handleClose} title="Provision new account">
      {context != null ? (
        <ProvisionStatus context={context} onClose={handleClose} />
      ) : (
        <>
          <Box sx={{ px: 8, flex: 1 }}>
            <Typography
              variant="labelSmall"
              sx={{ color: 'text.secondary', mb: 4, display: 'block' }}
            >
              Creates a new tenant account, owner user with credentials, company organisation, and
              seed data in one step. Tracked as an SqsJob.
            </Typography>

            <Stack spacing={3}>
              <Box>
                <Typography variant="labelSmall" sx={{ mb: 1, display: 'block' }}>
                  First name
                </Typography>
                <TextField
                  fullWidth
                  name="firstName"
                  placeholder="John"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                  disabled={formik.isSubmitting}
                />
              </Box>
              <Box>
                <Typography variant="labelSmall" sx={{ mb: 1, display: 'block' }}>
                  Last name
                </Typography>
                <TextField
                  fullWidth
                  name="lastName"
                  placeholder="Doe"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  disabled={formik.isSubmitting}
                />
              </Box>
              <Box>
                <Typography variant="labelSmall" sx={{ mb: 1, display: 'block' }}>
                  Owner email
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  placeholder="owner@example.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  disabled={formik.isSubmitting}
                />
              </Box>
              <Box>
                <Typography variant="labelSmall" sx={{ mb: 1, display: 'block' }}>
                  Company name
                </Typography>
                <TextField
                  fullWidth
                  name="organisationName"
                  placeholder="Acme Ltd."
                  value={formik.values.organisationName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.organisationName && Boolean(formik.errors.organisationName)}
                  helperText={formik.touched.organisationName && formik.errors.organisationName}
                  disabled={formik.isSubmitting}
                />
              </Box>
              <Box>
                <Typography variant="labelSmall" sx={{ mb: 1, display: 'block' }}>
                  Owner role in company
                </Typography>
                <SelectInput
                  label=""
                  placeholder="Select a role"
                  value={formik.values.role === '' ? undefined : formik.values.role}
                  onChange={value => formik.setFieldValue('role', value ?? '')}
                  onBlur={() => formik.setFieldTouched('role', true)}
                  options={roleOptions.map(option => ({ label: option.name, value: option.id }))}
                  error={formik.touched.role && Boolean(formik.errors.role)}
                  helperText={formik.touched.role ? formik.errors.role : undefined}
                  disabled={formik.isSubmitting || rolesLoading}
                />
              </Box>

              <Alert severity="info">
                If the email is new to citadel, a temporary password will be generated and shown
                once after provisioning — the new owner can change it on first login, after that we
                cannot retrieve it. If the email already has credentials in another tenant, the user
                will be linked into this account without a new password and signs in with their
                existing one.
              </Alert>

              {formik.status != null && (
                <Typography variant="labelSmall" sx={{ color: 'error.main' }}>
                  {formik.status}
                </Typography>
              )}
            </Stack>
          </Box>

          <DrawerModalButtonsWrapper>
            <Button
              variant="primary"
              onClick={() => formik.handleSubmit()}
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? 'Starting…' : 'Provision account'}
            </Button>
            <Button variant="secondary" onClick={handleClose} disabled={formik.isSubmitting}>
              Cancel
            </Button>
          </DrawerModalButtonsWrapper>
        </>
      )}
    </DrawerModal>
  );
}

interface ProvisionStatusProps {
  context: ProvisionContext;
  onClose: () => void;
}

function ProvisionStatus({ context, onClose }: ProvisionStatusProps) {
  const navigate = useNavigate();
  const { data, refetch } = useQuery(jobOneQuery, {
    variables: { id: context.jobId },
    fetchPolicy: 'cache-and-network'
  });

  const job = data?.sqsJobOne;
  const details = job?.accountProvision ?? null;
  const status = job?.status ?? 'pending';
  const progress = job?.progress ?? 0;
  const accountId = details?.accountId ?? null;
  const password = details?.temporaryPassword ?? context.password;
  const linkedExistingUser = details?.linkedExistingUser ?? context.linkedExistingUser;
  const email = details?.ownerEmail ?? context.email;
  const ownerName =
    details != null ? `${details.ownerFirstName} ${details.ownerLastName}`.trim() : null;
  const organisationName = details?.organisationName ?? null;
  const isInflight = status === 'pending' || status === 'processing';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  useEffect(() => {
    if (!isInflight) return;
    const interval = setInterval(() => {
      void refetch();
    }, 1500);
    return () => clearInterval(interval);
  }, [isInflight, refetch]);

  return (
    <>
      <Box sx={{ px: 8, flex: 1 }}>
        <Typography variant="labelSmall" sx={{ color: 'text.secondary', mb: 4, display: 'block' }}>
          Provisioning is running in the background. You can close this drawer once the password has
          been copied — the job will continue regardless and the password stays accessible on the
          account detail page until you dismiss it.
        </Typography>

        <Stack spacing={3}>
          {password == null && linkedExistingUser && (
            <Alert severity="info">
              {email} already had credentials in another tenant. They were linked into this account
              without a new temporary password — they can sign in with their existing credentials.
            </Alert>
          )}

          <CredentialField label="Owner email" value={email} />
          {ownerName != null && ownerName !== '' && (
            <CredentialField label="Owner name" value={ownerName} />
          )}
          {organisationName != null && <CredentialField label="Company" value={organisationName} />}
          {password != null && (
            <CredentialField label="Temporary password" value={password} secret copy />
          )}
          <CredentialField label="Job ID" value={context.jobId} subtle />
          <CredentialField
            label="Account ID"
            value={accountId ?? 'Creating…'}
            subtle={accountId == null}
            copy={accountId != null}
          />

          <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="labelSmall">Status</Typography>
              <StatusChip status={status} />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={isFailed ? 'error' : isCompleted ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography
              variant="labelSmall"
              sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
            >
              {progress}%
            </Typography>
          </Box>

          {isFailed && job?.errorMessage != null && (
            <Alert severity="error" sx={{ overflowWrap: 'anywhere' }}>
              {job.errorMessage}
            </Alert>
          )}
        </Stack>
      </Box>

      <DrawerModalButtonsWrapper>
        {isCompleted && accountId != null && (
          <Button variant="primary" onClick={() => navigate(`/accounts/${accountId}/overview`)}>
            Open account
          </Button>
        )}
        {password != null && (
          <Button
            variant={isCompleted || isFailed ? 'secondary' : 'primary'}
            onClick={async () => {
              await navigator.clipboard.writeText(password);
            }}
          >
            Copy password
          </Button>
        )}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </DrawerModalButtonsWrapper>
    </>
  );
}

interface CredentialFieldProps {
  label: string;
  value: string;
  monospace?: boolean;
  subtle?: boolean;
  copy?: boolean;
  // Renders the value masked behind an eye toggle (see SecretValue). Use for
  // sensitive values like temporary passwords.
  secret?: boolean;
}

function CredentialField(props: CredentialFieldProps) {
  if (props.secret === true) {
    return (
      <Box>
        <Typography variant="labelSmall" sx={{ mb: 1, display: 'block' }}>
          {props.label}
        </Typography>
        <SecretValue value={props.value} copy={props.copy === true} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="labelSmall" sx={{ mb: 1, display: 'block' }}>
        {props.label}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography
          sx={{
            flex: 1,
            fontFamily: props.monospace ? 'monospace' : undefined,
            fontSize: props.monospace ? '1.05rem' : undefined,
            color: props.subtle ? 'text.secondary' : undefined,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 1,
            overflowWrap: 'anywhere'
          }}
        >
          {props.value}
        </Typography>
        {props.copy === true && (
          <Button
            variant="secondary"
            onClick={async () => {
              await navigator.clipboard.writeText(props.value);
            }}
          >
            Copy
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function StatusChip({ status }: { status: string }) {
  const color =
    status === 'completed'
      ? 'success'
      : status === 'failed'
        ? 'error'
        : status === 'processing'
          ? 'info'
          : 'default';
  return <StatusBadge tone={color} label={status} />;
}

const provisionMutation = gql<AccountProvisionGQL>`
  mutation AccountProvision($input: AccountProvisionInput!) {
    accountProvision(input: $input) {
      jobId
      temporaryPassword
      linkedExistingUser
    }
  }
`;

const roleOptionsQuery = gql<ProvisionRoleOptionsGQL>`
  query ProvisionRoleOptions {
    organisationRoleTemplateOptions {
      id
      name
    }
  }
`;

const jobOneQuery = gql<ProvisionJobOneGQL>`
  query ProvisionJobOne($id: ID!) {
    sqsJobOne(id: $id) {
      id
      status
      progress
      errorMessage
      completedAt
      accountProvision {
        ownerEmail
        ownerFirstName
        ownerLastName
        organisationName
        accountId
        temporaryPassword
        linkedExistingUser
      }
    }
  }
`;
